import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/context/AuthContext';
import { appointmentService } from './AppointmentsService';
import { toast } from 'react-toastify';

const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

const DEFAULT_WORKING_HOURS = [
  { day: 'monday', startTime: '09:00', endTime: '17:00', isAvailable: true },
  { day: 'tuesday', startTime: '09:00', endTime: '17:00', isAvailable: true },
  { day: 'wednesday', startTime: '09:00', endTime: '17:00', isAvailable: true },
  { day: 'thursday', startTime: '09:00', endTime: '17:00', isAvailable: true },
  { day: 'friday', startTime: '09:00', endTime: '17:00', isAvailable: true },
  { day: 'saturday', startTime: '09:00', endTime: '13:00', isAvailable: true },
  { day: 'sunday', startTime: '09:00', endTime: '17:00', isAvailable: false }
];

const DoctorScheduleManager = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState({
    doctorId: currentUser._id,
    workingHours: DEFAULT_WORKING_HOURS,
    slotDuration: 30
  });
console.log(currentUser);
  useEffect(() => {
    const fetchDoctorSchedule = async () => {
      if (!currentUser || currentUser.role !== 'doctor') return;
      
      try {
        setLoading(true);
        const doctorSchedule = await appointmentService.getDoctorSchedule(currentUser._id);
        
        if (doctorSchedule) {
          setSchedule({
            doctorId: doctorSchedule.doctorId,
            workingHours: doctorSchedule.workingHours,
            slotDuration: doctorSchedule.slotDuration
          });
        }
      } catch (error) {
        console.error('Error fetching doctor schedule:', error);
        // Keep the default schedule
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorSchedule();
  }, [currentUser]);

  const handleSlotDurationChange = (e) => {
    setSchedule({
      ...schedule,
      slotDuration: parseInt(e.target.value, 10)
    });
  };

  const handleDayAvailabilityToggle = (day) => {
    setSchedule({
      ...schedule,
      workingHours: schedule.workingHours.map(hours => {
        if (hours.day === day) {
          return { ...hours, isAvailable: !hours.isAvailable };
        }
        return hours;
      })
    });
  };

  const handleTimeChange = (day, field, value) => {
    setSchedule({
      ...schedule,
      workingHours: schedule.workingHours.map(hours => {
        if (hours.day === day) {
          return { ...hours, [field]: value };
        }
        return hours;
      })
    });
  };

  const handleSaveSchedule = async () => {
    if (!currentUser || currentUser.role !== 'doctor') {
      toast.error('You must be logged in as a doctor to save your schedule');
      return;
    }

    try {
      setSaving(true);
      
      // Validate times before saving
      let hasError = false;
      schedule.workingHours.forEach(daySchedule => {
        if (daySchedule.isAvailable) {
          const start = daySchedule.startTime.split(':').map(Number);
          const end = daySchedule.endTime.split(':').map(Number);
          
          // Compare hours, or hours and minutes if hours are the same
          const startTime = start[0] * 60 + start[1];
          const endTime = end[0] * 60 + end[1];
          
          if (startTime >= endTime) {
            toast.error(`Invalid time range for ${daySchedule.day}. End time must be after start time.`);
            hasError = true;
          }
        }
      });
      
      if (hasError) {
        setSaving(false);
        return;
      }
      
      await appointmentService.updateDoctorSchedule(currentUser._id, schedule);
      toast.success('Schedule updated successfully');
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDay = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  if (!currentUser || currentUser.role !== 'doctor') {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
        <p className="text-yellow-700">Only doctors can manage appointment schedules.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Manage Your Appointment Schedule</h2>
      
      {loading ? (
        <div className="flex justify-center my-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appointment Duration (minutes)
            </label>
            <select
              value={schedule.slotDuration}
              onChange={handleSlotDurationChange}
              className="w-full md:w-1/3 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              This will determine how long each appointment slot is.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-medium mb-4">Working Hours</h3>
            <div className="space-y-4">
              {DAYS_OF_WEEK.map((day) => {
                const dayHours = schedule.workingHours.find(h => h.day === day) || 
                  DEFAULT_WORKING_HOURS.find(h => h.day === day);
                
                return (
                  <div key={day} className="border-b pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`available-${day}`}
                          checked={dayHours.isAvailable}
                          onChange={() => handleDayAvailabilityToggle(day)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`available-${day}`} className="ml-2 font-medium">
                          {formatDay(day)}
                        </label>
                      </div>
                      <div className="text-sm text-gray-500">
                        {dayHours.isAvailable ? 'Available' : 'Unavailable'}
                      </div>
                    </div>

                    {dayHours.isAvailable && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">Start Time</label>
                          <input
                            type="time"
                            value={dayHours.startTime}
                            onChange={(e) => handleTimeChange(day, 'startTime', e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">End Time</label>
                          <input
                            type="time"
                            value={dayHours.endTime}
                            onChange={(e) => handleTimeChange(day, 'endTime', e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSchedule}
              disabled={saving}
              className={`px-4 py-2 rounded-md text-white font-medium ${
                saving 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {saving ? (
                <>
                  <span className="inline-block mr-2">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                  Saving...
                </>
              ) : 'Save Schedule'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DoctorScheduleManager;